import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import {
  Plus,
  Search,
  Download,
  Eye,
  Edit,
  Trash2,
  X,
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Calendar,
  User,
  Shield,
  ClipboardCheck,
  History,
  Bell,
  Settings,
  Loader2,
  ChevronDown,
  ChevronUp,
  Check,
  MapPin,
  Clock,
  Camera,
  FileWarning,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Default checklist items based on the uploaded document
const DEFAULT_CHECKLIST_ITEMS = [
  { category: 'Generelt', items: [
    'Sjekk at alle skilt og oppslag er leselige og på plass',
    'Kontroller at førstehjelpsutstyr er komplett og tilgjengelig',
    'Verifiser at brannslukkingsapparat er på plass og godkjent',
    'Sjekk at nødutganger er merket og frie for hindringer'
  ]},
  { category: 'Skytebane / Standplass', items: [
    'Kontroller skiveanlegget – funksjon og sikkerhet',
    'Sjekk standplasser for skader eller slitasje',
    'Verifiser at skytebord/benker er i god stand',
    'Kontroller belysning på standplass og nedover banen'
  ]},
  { category: 'Sikkerhetsinstallasjoner', items: [
    'Sjekk kulefang/backstop for skader',
    'Kontroller sidevegger og tak for hull eller skader',
    'Verifiser at sikkerhetsmarkeringer er synlige',
    'Test eventuelle elektroniske sikkerhetssystemer'
  ]},
  { category: 'Dokumentasjon', items: [
    'Oppdater skytebanelogg',
    'Gjennomgå HMS-dokumentasjon',
    'Kontroller at forsikringspapirer er gyldige',
    'Verifiser at alle tillatelser er oppdaterte'
  ]}
];

const RangeManagement = () => {
  const { user, isAdmin, isOrgAdmin, isRangeOfficer } = useAuth();
  const [members, setMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const hasAccess = isAdmin || isOrgAdmin || isRangeOfficer;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('inspections');
  
  // Data states
  const [loading, setLoading] = useState(true);
  const [inspections, setInspections] = useState([]);
  const [maintenancePlans, setMaintenancePlans] = useState([]);
  const [hmsDocuments, setHmsDocuments] = useState(null);
  const [rangeManager, setRangeManager] = useState(null);
  
  // Modal states
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showHmsModal, setShowHmsModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  
  // Inspection form
  const [inspectionForm, setInspectionForm] = useState({
    inspection_date: new Date().toISOString().split('T')[0],
    inspector_name: '',
    inspection_type: 'annual', // annual, semi-annual
    range_name: '',
    findings: '',
    deviations: '',
    status: 'passed', // passed, failed, needs_attention
    images: []
  });
  
  // Checklist state
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistYear, setChecklistYear] = useState(new Date().getFullYear());
  const [checklistHistory, setChecklistHistory] = useState([]);
  
  // HMS state
  const [hmsContent, setHmsContent] = useState('');
  const [hmsFiles, setHmsFiles] = useState([]);
  
  // File upload state
  const [uploading, setUploading] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (hasAccess) {
      loadAllData();
    }
  }, [hasAccess]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadInspections(),
      loadMaintenancePlans(),
      loadHmsDocuments(),
      loadRangeManager(),
      loadMembers(),
      loadChecklistItems()
    ]);
    setLoading(false);
  };

  const loadInspections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/range/inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data || []);
    } catch (error) {
      console.error('Error loading inspections:', error);
    }
  };

  const loadMaintenancePlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/range/maintenance-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaintenancePlans(response.data || []);
    } catch (error) {
      console.error('Error loading maintenance plans:', error);
    }
  };

  const loadHmsDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/range/hms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHmsDocuments(response.data);
      setHmsContent(response.data?.content || '');
      setHmsFiles(response.data?.files || []);
    } catch (error) {
      console.error('Error loading HMS documents:', error);
    }
  };

  const loadRangeManager = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/range/manager`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRangeManager(response.data);
    } catch (error) {
      console.error('Error loading range manager:', error);
    }
  };

  const loadMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show approved and active members, then sort alphabetically
      const activeMembers = (response.data || [])
        .filter(m => m.approved && m.active !== false)
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'nb'));
      setMembers(activeMembers);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };
  
  // Filter members based on search query
  const filteredMembers = members.filter(member => 
    (member.full_name || '').toLowerCase().includes(memberSearchQuery.toLowerCase())
  );

  const loadChecklistItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/range/checklist?year=${checklistYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.items?.length > 0) {
        setChecklistItems(response.data.items);
      } else {
        // Use default checklist
        setChecklistItems(DEFAULT_CHECKLIST_ITEMS.flatMap(cat => 
          cat.items.map(item => ({ category: cat.category, text: item, checked: false, checked_by: null, checked_at: null }))
        ));
      }
      setChecklistHistory(response.data?.history || []);
    } catch (error) {
      console.error('Error loading checklist:', error);
      // Use default checklist
      setChecklistItems(DEFAULT_CHECKLIST_ITEMS.flatMap(cat => 
        cat.items.map(item => ({ category: cat.category, text: item, checked: false, checked_by: null, checked_at: null }))
      ));
    }
  };

  // Inspection handlers
  const handleAddInspection = () => {
    setEditingInspection(null);
    setInspectionForm({
      inspection_date: new Date().toISOString().split('T')[0],
      inspector_name: user?.full_name || '',
      inspection_type: 'annual',
      range_name: '',
      findings: '',
      deviations: '',
      status: 'passed',
      images: []
    });
    setShowInspectionModal(true);
  };

  const handleEditInspection = (inspection) => {
    setEditingInspection(inspection);
    setInspectionForm({
      inspection_date: inspection.inspection_date || '',
      inspector_name: inspection.inspector_name || '',
      inspection_type: inspection.inspection_type || 'annual',
      range_name: inspection.range_name || '',
      findings: inspection.findings || '',
      deviations: inspection.deviations || '',
      status: inspection.status || 'passed',
      images: inspection.images || []
    });
    setShowInspectionModal(true);
  };

  const handleSaveInspection = async () => {
    if (!inspectionForm.inspection_date || !inspectionForm.inspector_name) {
      toast.error('Dato og kontrollør er påkrevd');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (editingInspection) {
        await axios.put(
          `${API_BASE}/api/club-operations/range/inspections/${editingInspection.id}`,
          inspectionForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Kontroll oppdatert');
      } else {
        await axios.post(
          `${API_BASE}/api/club-operations/range/inspections`,
          inspectionForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Kontroll registrert');
      }
      setShowInspectionModal(false);
      loadInspections();
    } catch (error) {
      console.error('Error saving inspection:', error);
      toast.error('Kunne ikke lagre kontroll');
    }
  };

  const handleDeleteInspection = async (inspectionId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne kontrollen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/club-operations/range/inspections/${inspectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kontroll slettet');
      loadInspections();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error('Kunne ikke slette kontroll');
    }
  };

  // Image upload for inspections
  const handleInspectionImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploading(true);
    const newImages = [...inspectionForm.images];
    
    for (const file of files) {
      try {
        // Compress image
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1200,
          useWebWorker: true,
          fileType: 'image/jpeg'
        };
        const compressedFile = await imageCompression(file, options);
        
        // Upload to server
        const formData = new FormData();
        formData.append('file', compressedFile, `inspection_${Date.now()}.jpg`);
        
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_BASE}/api/club-operations/range/upload-image`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        newImages.push(response.data.url);
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error(`Kunne ikke laste opp ${file.name}`);
      }
    }
    
    setInspectionForm(prev => ({ ...prev, images: newImages }));
    setUploading(false);
  };

  const handleDeleteInspectionImage = async (imageUrl) => {
    if (!window.confirm('Er du sikker på at du vil slette dette bildet?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/club-operations/range/delete-image`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { image_url: imageUrl }
      });
      
      setInspectionForm(prev => ({
        ...prev,
        images: prev.images.filter(img => img !== imageUrl)
      }));
      toast.success('Bilde slettet');
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Kunne ikke slette bilde');
    }
  };

  // Checklist handlers
  const handleChecklistToggle = async (index) => {
    const updatedItems = [...checklistItems];
    updatedItems[index] = {
      ...updatedItems[index],
      checked: !updatedItems[index].checked,
      checked_by: !updatedItems[index].checked ? user?.full_name : null,
      checked_at: !updatedItems[index].checked ? new Date().toISOString() : null
    };
    setChecklistItems(updatedItems);
    
    // Auto-save
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/club-operations/range/checklist`,
        { year: checklistYear, items: updatedItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving checklist:', error);
    }
  };

  const handleAddChecklistItem = () => {
    const newItem = {
      category: 'Egendefinert',
      text: '',
      checked: false,
      checked_by: null,
      checked_at: null,
      isNew: true
    };
    setChecklistItems([...checklistItems, newItem]);
  };

  const handleUpdateChecklistItemText = (index, text) => {
    const updatedItems = [...checklistItems];
    updatedItems[index] = { ...updatedItems[index], text, isNew: false };
    setChecklistItems(updatedItems);
  };

  const handleRemoveChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleSaveChecklist = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/club-operations/range/checklist`,
        { year: checklistYear, items: checklistItems.filter(item => item.text.trim()) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sjekkliste lagret');
    } catch (error) {
      console.error('Error saving checklist:', error);
      toast.error('Kunne ikke lagre sjekkliste');
    }
  };

  // HMS handlers
  const handleSaveHms = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/club-operations/range/hms`,
        { content: hmsContent, files: hmsFiles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('HMS-dokumentasjon lagret');
      setShowHmsModal(false);
    } catch (error) {
      console.error('Error saving HMS:', error);
      toast.error('Kunne ikke lagre HMS-dokumentasjon');
    }
  };

  const handleHmsFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE}/api/club-operations/range/upload-hms-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setHmsFiles([...hmsFiles, { name: file.name, url: response.data.url }]);
      toast.success('Fil lastet opp');
    } catch (error) {
      console.error('Error uploading HMS file:', error);
      toast.error('Kunne ikke laste opp fil');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteHmsFile = async (fileUrl) => {
    if (!window.confirm('Er du sikker på at du vil slette denne filen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/club-operations/range/delete-hms-file`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { file_url: fileUrl }
      });
      
      setHmsFiles(hmsFiles.filter(f => f.url !== fileUrl));
      toast.success('Fil slettet');
    } catch (error) {
      console.error('Error deleting HMS file:', error);
      toast.error('Kunne ikke slette fil');
    }
  };

  // Range manager handlers
  const handleSetRangeManager = async (memberId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/club-operations/range/manager`,
        { member_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadRangeManager();
      toast.success('Baneansvarlig oppdatert');
    } catch (error) {
      console.error('Error setting range manager:', error);
      toast.error('Kunne ikke oppdatere baneansvarlig');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'passed':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Godkjent</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">Ikke godkjent</span>;
      case 'needs_attention':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">Krever oppfølging</span>;
      default:
        return null;
    }
  };

  // Get last inspection
  const lastInspection = inspections[0];

  if (!hasAccess) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Du har ikke tilgang til denne modulen</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Last Inspection Banner */}
      {lastInspection && (
        <Card className={`border-2 ${
          lastInspection.status === 'passed' ? 'bg-green-900/20 border-green-500/50' :
          lastInspection.status === 'failed' ? 'bg-red-900/20 border-red-500/50' :
          'bg-yellow-900/20 border-yellow-500/50'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  lastInspection.status === 'passed' ? 'bg-green-500/20' :
                  lastInspection.status === 'failed' ? 'bg-red-500/20' :
                  'bg-yellow-500/20'
                }`}>
                  {lastInspection.status === 'passed' ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : lastInspection.status === 'failed' ? (
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  ) : (
                    <Clock className="w-6 h-6 text-yellow-400" />
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">
                    Siste kontroll: {formatDate(lastInspection.inspection_date)}
                  </p>
                  <p className="text-gray-400 text-sm">
                    Utført av: {lastInspection.inspector_name}
                  </p>
                </div>
              </div>
              {getStatusBadge(lastInspection.status)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Range Manager Selection */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-yellow-500" />
              <span className="text-white">Baneansvarlig:</span>
              <span className="text-yellow-400 font-medium">
                {rangeManager?.name || 'Ikke valgt'}
              </span>
            </div>
            <Select
              value={rangeManager?.member_id || ''}
              onValueChange={(value) => {
                handleSetRangeManager(value);
                setMemberSearchQuery(''); // Clear search after selection
              }}
            >
              <SelectTrigger className="w-[220px] bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Velg baneansvarlig" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 max-h-[300px]">
                {/* Search field */}
                <div className="p-2 border-b border-gray-700 sticky top-0 bg-gray-800">
                  <input
                    type="text"
                    placeholder="Søk etter medlem..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {/* Members list */}
                {filteredMembers.length > 0 ? (
                  filteredMembers.map(member => (
                    <SelectItem key={member.id} value={member.id} className="text-white hover:bg-gray-700">
                      {member.full_name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-3 text-gray-400 text-sm text-center">
                    Ingen medlemmer funnet
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <p className="text-gray-500 text-xs mt-2">
            <Bell className="w-3 h-3 inline mr-1" />
            Automatisk påminnelse sendes 10. mai hvert år
          </p>
        </CardContent>
      </Card>

      {/* Sub-navigation tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800 border-gray-700 flex-wrap h-auto">
          <TabsTrigger value="inspections" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900">
            <Shield className="w-4 h-4 mr-2" />
            Sikkerhetskontroller
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Vedlikeholdsplaner
          </TabsTrigger>
          <TabsTrigger value="hms" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900">
            <FileText className="w-4 h-4 mr-2" />
            HMS-dokumentasjon
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900">
            <History className="w-4 h-4 mr-2" />
            Kontrollhistorikk
          </TabsTrigger>
        </TabsList>

        {/* Sikkerhetskontroller Tab */}
        <TabsContent value="inspections" className="mt-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-yellow-500" />
                    Sikkerhetskontroller
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Årlige og halvårlige sikkerhetskontroller av skytebaner
                  </CardDescription>
                </div>
                <button
                  onClick={handleAddInspection}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Ny kontroll
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {inspections.length > 0 ? (
                <div className="space-y-4">
                  {inspections.slice(0, 10).map((inspection) => (
                    <div
                      key={inspection.id}
                      className="bg-gray-700/50 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white font-medium">
                              {inspection.range_name || 'Skytebane'}
                            </p>
                            {getStatusBadge(inspection.status)}
                          </div>
                          <p className="text-gray-400 text-sm flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(inspection.inspection_date)}
                            <span className="mx-2">•</span>
                            <User className="w-3 h-3" />
                            {inspection.inspector_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditInspection(inspection)}
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                            title="Rediger"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteInspection(inspection.id)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Slett"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {inspection.findings && (
                        <p className="text-gray-300 text-sm mb-2">{inspection.findings}</p>
                      )}
                      
                      {inspection.deviations && (
                        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300 mb-2">
                          <FileWarning className="w-4 h-4 inline mr-1" />
                          Avvik: {inspection.deviations}
                        </div>
                      )}
                      
                      {inspection.images?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {inspection.images.map((img, idx) => (
                            <div key={idx} className="relative group">
                              <img
                                src={img}
                                alt={`Avvik ${idx + 1}`}
                                className="w-16 h-16 object-cover rounded cursor-pointer"
                                onClick={() => window.open(img, '_blank')}
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                <a
                                  href={img}
                                  download
                                  className="p-1 text-white hover:text-green-400"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Download className="w-4 h-4" />
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Ingen kontroller registrert</p>
                  <button
                    onClick={handleAddInspection}
                    className="mt-4 text-yellow-500 hover:text-yellow-400"
                  >
                    Registrer første kontroll
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vedlikeholdsplaner Tab */}
        <TabsContent value="maintenance" className="mt-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-yellow-500" />
                    Vedlikeholdssjekkliste {checklistYear}
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Årlig / halvårlig ettersyn av skytebane
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={checklistYear.toString()}
                    onValueChange={(val) => {
                      setChecklistYear(parseInt(val));
                      loadChecklistItems();
                    }}
                  >
                    <SelectTrigger className="w-[100px] bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {[2024, 2025, 2026, 2027].map(year => (
                        <SelectItem key={year} value={year.toString()} className="text-white">
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={handleSaveChecklist}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500 transition-colors"
                  >
                    Lagre
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Group items by category */}
                {Object.entries(
                  checklistItems.reduce((acc, item, idx) => {
                    const cat = item.category || 'Annet';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push({ ...item, index: idx });
                    return acc;
                  }, {})
                ).map(([category, items]) => (
                  <div key={category}>
                    <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {items.map((item) => (
                        <div
                          key={item.index}
                          className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                            item.checked ? 'bg-green-900/20' : 'bg-gray-700/50'
                          }`}
                        >
                          <button
                            onClick={() => handleChecklistToggle(item.index)}
                            className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              item.checked
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-500 hover:border-yellow-500'
                            }`}
                          >
                            {item.checked && <Check className="w-3 h-3 text-white" />}
                          </button>
                          <div className="flex-1">
                            {item.isNew ? (
                              <input
                                type="text"
                                value={item.text}
                                onChange={(e) => handleUpdateChecklistItemText(item.index, e.target.value)}
                                placeholder="Skriv inn nytt punkt..."
                                className="w-full bg-transparent border-b border-gray-600 text-white focus:outline-none focus:border-yellow-500"
                                autoFocus
                              />
                            ) : (
                              <p className={`${item.checked ? 'text-gray-400 line-through' : 'text-white'}`}>
                                {item.text}
                              </p>
                            )}
                            {item.checked && item.checked_by && (
                              <p className="text-xs text-gray-500 mt-1">
                                Sjekket av {item.checked_by} - {formatDate(item.checked_at)}
                              </p>
                            )}
                          </div>
                          {item.category === 'Egendefinert' && (
                            <button
                              onClick={() => handleRemoveChecklistItem(item.index)}
                              className="p-1 text-gray-500 hover:text-red-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={handleAddChecklistItem}
                  className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-yellow-500 hover:text-yellow-500 transition-colors"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  Legg til eget punkt
                </button>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-700">
                <p className="text-gray-400 text-sm">
                  <CheckCircle className="w-4 h-4 inline mr-1 text-green-500" />
                  {checklistItems.filter(i => i.checked).length} av {checklistItems.length} punkter fullført
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HMS-dokumentasjon Tab */}
        <TabsContent value="hms" className="mt-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-yellow-500" />
                    HMS-dokumentasjon
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Klubbens HMS-instrukser og dokumenter
                  </CardDescription>
                </div>
                <button
                  onClick={() => setShowHmsModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Rediger
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {/* HMS Content */}
              {hmsContent ? (
                <div className="prose prose-invert max-w-none mb-6">
                  <div className="whitespace-pre-wrap text-gray-300 bg-gray-700/50 p-4 rounded-lg">
                    {hmsContent}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 mb-6">
                  <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Ingen HMS-instrukser lagt inn</p>
                  <button
                    onClick={() => setShowHmsModal(true)}
                    className="mt-4 text-yellow-500 hover:text-yellow-400"
                  >
                    Legg inn HMS-instrukser
                  </button>
                </div>
              )}
              
              {/* HMS Files */}
              {hmsFiles.length > 0 && (
                <div>
                  <h3 className="text-white font-medium mb-3">Vedlagte dokumenter</h3>
                  <div className="space-y-2">
                    {hmsFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg group"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-400" />
                          <span className="text-gray-300">{file.name}</span>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-white rounded-lg transition-colors"
                            title="Forhåndsvis"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={file.url}
                            download
                            className="p-2 text-gray-400 hover:text-green-400 rounded-lg transition-colors"
                            title="Last ned"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteHmsFile(file.url)}
                            className="p-2 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
                            title="Slett"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Kontrollhistorikk Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-yellow-500" />
                Kontrollhistorikk
              </CardTitle>
              <CardDescription className="text-gray-400">
                Oversikt over alle gjennomførte sikkerhetskontroller
              </CardDescription>
            </CardHeader>
            <CardContent>
              {inspections.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-700">
                        <th className="text-left py-3 px-2">Dato</th>
                        <th className="text-left py-3 px-2">Bane</th>
                        <th className="text-left py-3 px-2">Type</th>
                        <th className="text-left py-3 px-2">Kontrollør</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Avvik</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inspections.map((inspection) => (
                        <tr key={inspection.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                          <td className="py-3 px-2 text-white">
                            {formatDate(inspection.inspection_date)}
                          </td>
                          <td className="py-3 px-2 text-gray-300">
                            {inspection.range_name || 'Skytebane'}
                          </td>
                          <td className="py-3 px-2 text-gray-300">
                            {inspection.inspection_type === 'annual' ? 'Årlig' : 'Halvårlig'}
                          </td>
                          <td className="py-3 px-2 text-gray-300">
                            {inspection.inspector_name}
                          </td>
                          <td className="py-3 px-2">
                            {getStatusBadge(inspection.status)}
                          </td>
                          <td className="py-3 px-2 text-gray-400">
                            {inspection.deviations ? (
                              <span className="text-red-400">{inspection.deviations.slice(0, 50)}...</span>
                            ) : (
                              <span className="text-green-400">Ingen</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Ingen kontrollhistorikk</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Inspection Modal */}
      <Dialog open={showInspectionModal} onOpenChange={setShowInspectionModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingInspection ? 'Rediger kontroll' : 'Ny sikkerhetskontroll'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Registrer årlig eller halvårlig sikkerhetskontroll
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Dato *</label>
                <input
                  type="date"
                  value={inspectionForm.inspection_date}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, inspection_date: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Kontrollør *</label>
                <input
                  type="text"
                  value={inspectionForm.inspector_name}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, inspector_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="Navn på kontrollør"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bane</label>
                <input
                  type="text"
                  value={inspectionForm.range_name}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, range_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="F.eks. Pistolbane, Riflebane"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Type kontroll</label>
                <Select
                  value={inspectionForm.inspection_type}
                  onValueChange={(val) => setInspectionForm(prev => ({ ...prev, inspection_type: val }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="annual" className="text-white">Årlig</SelectItem>
                    <SelectItem value="semi-annual" className="text-white">Halvårlig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Status</label>
              <Select
                value={inspectionForm.status}
                onValueChange={(val) => setInspectionForm(prev => ({ ...prev, status: val }))}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="passed" className="text-white">Godkjent</SelectItem>
                  <SelectItem value="needs_attention" className="text-white">Krever oppfølging</SelectItem>
                  <SelectItem value="failed" className="text-white">Ikke godkjent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Funn / Observasjoner</label>
              <textarea
                value={inspectionForm.findings}
                onChange={(e) => setInspectionForm(prev => ({ ...prev, findings: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                rows={3}
                placeholder="Beskriv observasjoner fra kontrollen..."
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Avvik</label>
              <textarea
                value={inspectionForm.deviations}
                onChange={(e) => setInspectionForm(prev => ({ ...prev, deviations: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 border-red-500/50"
                rows={2}
                placeholder="Noter eventuelle avvik som må utbedres..."
              />
            </div>
            
            {/* Image upload */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Bilder av avvik</label>
              
              {inspectionForm.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {inspectionForm.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={img}
                        alt={`Avvik ${idx + 1}`}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                        <a
                          href={img}
                          download
                          className="p-1 text-white hover:text-green-400"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteInspectionImage(img)}
                          className="p-1 text-white hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <label className="flex items-center gap-2 px-4 py-3 bg-gray-700 border border-dashed border-gray-600 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleInspectionImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
                ) : (
                  <Camera className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-gray-400">
                  {uploading ? 'Laster opp...' : 'Last opp bilder av avvik'}
                </span>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowInspectionModal(false)}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Avbryt
            </button>
            <button
              onClick={handleSaveInspection}
              className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400"
            >
              {editingInspection ? 'Oppdater' : 'Lagre'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HMS Modal */}
      <Dialog open={showHmsModal} onOpenChange={setShowHmsModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Rediger HMS-dokumentasjon</DialogTitle>
            <DialogDescription className="text-gray-400">
              Skriv eller lim inn klubbens HMS-instrukser
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">HMS-instrukser</label>
              <textarea
                value={hmsContent}
                onChange={(e) => setHmsContent(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 font-mono text-sm"
                rows={15}
                placeholder="Skriv eller lim inn HMS-instrukser her..."
              />
            </div>
            
            {/* File upload */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Last opp dokumenter</label>
              
              {hmsFiles.length > 0 && (
                <div className="space-y-2 mb-3">
                  {hmsFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                      <span className="text-gray-300 text-sm">{file.name}</span>
                      <button
                        onClick={() => handleDeleteHmsFile(file.url)}
                        className="p-1 text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <label className="flex items-center gap-2 px-4 py-3 bg-gray-700 border border-dashed border-gray-600 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleHmsFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
                ) : (
                  <Upload className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-gray-400">
                  {uploading ? 'Laster opp...' : 'Last opp HMS-dokument'}
                </span>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowHmsModal(false)}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Avbryt
            </button>
            <button
              onClick={handleSaveHms}
              className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400"
            >
              Lagre
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RangeManagement;
